import React, { createContext, useContext, useRef, useState } from 'react';
import { StackScrollTool, utilities, ToolGroupManager } from '@cornerstonejs/tools';
import saveWheelConfig from '../api/saveWheelConfig';
import { useConfig, logDebug } from '@worklist-2/core/src';
import { tools } from '../features/Wheel/tools';
import getDefaultRenderingEngine from '../cornerstone/getDefaultRenderingEngine';
import { useSearchParams } from 'react-router-dom';
import { useCustomToolBarStoreSelector } from '../features/CustomToolbar/context/zustand/CustomToolBarZustandProvider';
import { useImageViewerLayoutStoreSelector } from '../features/CustomToolbar/context/zustand/ImageViewerLayoutZustandProvider';
import { useBooleanFlagValue } from '@rs-core/hooks/useFlags';
import { useCalibrationToolContext } from '../features/CalibrationTool/CalibrationToolContext';
import { useScoutLinesStoreSelector } from './ScoutLinesContext';

import ScaleOverlayTool from '../cornerstoneTools/ScaleOverlayTool';
import ReferenceLinesTool from '../cornerstoneTools/ReferenceLinesTool';

const ImageViewerWheelContext = createContext({});

const ImageViewerWheelContextProvider = ({ children }) => {
	const __config = useConfig();
	const wonIvCustomtoolbar = useBooleanFlagValue('WON-IV-CUSTOMTOOLBAR');
	const wonIvCinePerformance = useBooleanFlagValue('won-iv-cine-performance');
	const [searchParams] = useSearchParams();
	const isSharedStudy = searchParams.get('SharedStudy');
	const setHotkeyConfig = useCustomToolBarStoreSelector(state => state.setHotkeyConfig);
	const addActiveCine = useImageViewerLayoutStoreSelector(state => state.addActiveCine);
	const playCineZustand = useImageViewerLayoutStoreSelector(state => state.playCine);
	const stopCineZustand = useImageViewerLayoutStoreSelector(state => state.stopCine);
	const removeActiveCine = useImageViewerLayoutStoreSelector(state => state.removeActiveCine);

	const [wheelSettings, setWheelSettings] = useState({
		wheelTools: tools,
		wheelAdjustmentToolsCount: 8,
		wheelMarkupToolsCount: 8,
	});
	const [lastAdjustmentTool, setLastAdjustmentTool] = useState();
	const [activeTool, setActiveTool] = useState(StackScrollTool.toolName);
	const [isShownWheelCustomizationDrawer, setIsShownWheelCustomizationDrawer] = useState(false);
	const [activeCine, setActiveCine] = useState([]);
	const activeToolRef = useRef(activeTool);
	activeToolRef.current = activeTool;
	const { isShownCalibrationRuler } = useCalibrationToolContext();
	const isScoutLinesEnabled = useScoutLinesStoreSelector(state => state.scoutLines);

	const submitWheelConfig = wheelConfig => {
		if (isSharedStudy) {
			return;
		}
		wonIvCustomtoolbar && setHotkeyConfig(wheelConfig.wheelTools);
		return saveWheelConfig({ __config, wheelConfig });
	};

	const enableOverlay = viewportId => {
		// To Enable overlay tools for specific viewport after playback
		const toolGroups = ToolGroupManager.getToolGroup(viewportId);
		if (!toolGroups) return;
		if (isShownCalibrationRuler) {
			// Calibration Ruler tool
			toolGroups.setToolEnabled(ScaleOverlayTool.toolName);
		}
		if (isScoutLinesEnabled) {
			// Scout liner Tool
			toolGroups.setToolEnabled(ReferenceLinesTool.toolName);
		}
	};

	const disableOverlay = viewportId => {
		// To disable overlay tools for specific viewport during playback
		const toolGroups = ToolGroupManager.getToolGroup(viewportId);
		if (!toolGroups) return;

		if (isShownCalibrationRuler) {
			toolGroups.setToolDisabled(ScaleOverlayTool.toolName);
		}
		if (isScoutLinesEnabled) {
			toolGroups.setToolDisabled(ReferenceLinesTool.toolName);
		}
	};

	const createCine = ({ element }) =>
		new Promise(resolve => {
			const renderingEngine = getDefaultRenderingEngine();
			if (!renderingEngine) return;

			const viewport = renderingEngine.getViewport(element.id);
			if (!viewport) return;

			const imageIds = viewport.getImageIds();

			viewport
				.loadImages(imageIds || [])
				.then(() => {
					if (wonIvCinePerformance) {
						disableOverlay(element.id);
					}
					requestAnimationFrame(() => {
						utilities.cine.playClip(element, {
							framesPerSecond: 30,
							waitForRendered: false, // Changed to false for better performance
							loop: true,
						});
					});
				})
				.finally(() => resolve());

			if (wonIvCustomtoolbar) {
				addActiveCine({
					id: element.id,
					isActive: true,
				});
			}

			setActiveCine(prevState => [
				...prevState,
				{
					id: element.id,
					isActive: true,
				},
			]);
		});

	const removeCine = ({ element }) => {
		utilities.cine.stopClip(element);
		if (wonIvCinePerformance) {
			enableOverlay(element.id);
		}
		wonIvCustomtoolbar && removeActiveCine(element);
		setActiveCine(prevState => prevState.filter(item => item.id !== element.id));
	};

	const playCine = ({ element, options = {} }) => {
		const renderingEngine = getDefaultRenderingEngine();
		if (!renderingEngine) return;

		const viewport = renderingEngine.getViewport(element.id);
		if (!viewport) return;

		// First disable overlay
		if (wonIvCinePerformance) {
			disableOverlay(element.id);
		}
		// Add a small delay to ensure overlay is fully disabled
		setTimeout(() => {
			try {
				// Force a viewport reset
				// viewport.reset();
				// Ensure the viewport is properly rendered
				viewport.render();

				const imageIds = viewport.getImageIds();
				const fps = parseInt(options.framesPerSecond, 10) || 30;

				logDebug('IV::ImageViewerWheelContextProvider', 'playCine - FPS', {
					fps,
				});

				// Check if all images are loaded
				const areImagesLoaded = imageIds.every(id => {
					try {
						return viewport.getImageData(id) !== undefined;
					} catch (e) {
						return false;
					}
				});

				const startPlayback = () => {
					// Initialize playback with utilities.cine.playClip
					utilities.cine.playClip(element, {
						framesPerSecond: fps,
						waitForRendered: false, // Changed to false for better performance
						loop: true,
					});

					// Update state once at the start
					if (wonIvCustomtoolbar) {
						playCineZustand(element);
					}

					setActiveCine(prevState =>
						prevState.map(item => (item.id === element.id ? { ...item, isActive: true } : item))
					);
				};

				if (!areImagesLoaded) {
					viewport.loadImages(imageIds).then(() => {
						requestAnimationFrame(startPlayback);
					});
				} else {
					requestAnimationFrame(startPlayback);
				}
			} catch (error) {
				// Re-enable overlay if there's an error
				if (wonIvCinePerformance) {
					enableOverlay(element.id);
				}
			}
		}, 50); // 50ms delay
	};

	const stopCine = ({ element }) => {
		try {
			utilities.cine.stopClip(element);
			// Add a small delay before re-enabling overlay
			if (wonIvCinePerformance) {
				setTimeout(() => {
					enableOverlay(element.id);
				}, 50);
			}

			if (wonIvCustomtoolbar) {
				stopCineZustand(element);
			}

			setActiveCine(prevState =>
				prevState.map(item => (item.id === element.id ? { ...item, isActive: false } : item))
			);
		} catch (error) {
			// Ensure overlay is re-enabled even if there's an error
			if (wonIvCinePerformance) {
				enableOverlay(element.id);
			}
		}
	};

	const toggleCine = ({ element }) => {
		const cineItem = activeCine.find(item => item.id === element.id);

		if (!cineItem) {
			return createCine({ element });
		}

		if (cineItem.isActive) {
			stopCine({ element });
		} else {
			playCine({ element });
		}
	};

	return (
		<ImageViewerWheelContext.Provider
			value={{
				wheelSettings,
				setWheelSettings,
				submitWheelConfig,
				activeTool,
				setActiveTool,
				activeToolRef,
				isShownWheelCustomizationDrawer,
				setIsShownWheelCustomizationDrawer,
				removeCine,
				activeCine,
				setActiveCine,
				createCine,
				playCine,
				stopCine,
				toggleCine,
				lastAdjustmentTool,
				setLastAdjustmentTool,
			}}
		>
			{children}
		</ImageViewerWheelContext.Provider>
	);
};

const useImageViewerWheelContext = () => useContext(ImageViewerWheelContext);

export { ImageViewerWheelContext, useImageViewerWheelContext, ImageViewerWheelContextProvider };
